/**
 * What the rel-vol baselines actually did on the chart's bars, published by
 * ChartView for the settings rows (issue #593): forced time of day must never
 * just show nothing, and auto must say when it is running on trailing.
 * Display state only, not persisted.
 */
import { create } from 'zustand';
import { relVolMisses, todMissText, type RelVolBaseline } from '../utils/relVolBaseline';

export interface RelVolSummary {
  /** Chart bars rated on time of day / on the trailing mean. */
  tod: number;
  trailing: number;
  /** Chart bars forced time of day could not rate. */
  misses: number;
  /** Sessions available for the newest such bar. */
  lastMissSamples: number | null;
}

export function summarizeRelVols(map: ReadonlyMap<number, { basis: 'tod' | 'trailing' }> | null | undefined): RelVolSummary | null {
  if (!map) return null;
  let tod = 0;
  let trailing = 0;
  for (const r of map.values()) {
    if (r.basis === 'tod') tod++;
    else trailing++;
  }
  let last: number | null = null;
  let lastT = -Infinity;
  const misses = relVolMisses(map);
  for (const [t, m] of misses) {
    if (t > lastT) {
      lastT = t;
      last = m.samples;
    }
  }
  return { tod, trailing, misses: misses.size, lastMissSamples: last };
}

const sameSummary = (a: RelVolSummary | null, b: RelVolSummary | null): boolean =>
  a === b || (!!a && !!b && a.tod === b.tod && a.trailing === b.trailing && a.misses === b.misses && a.lastMissSamples === b.lastMissSamples);

interface RelVolStatusState {
  vest: RelVolSummary | null;
  cme: RelVolSummary | null;
  setVest: (s: RelVolSummary | null) => void;
  setCme: (s: RelVolSummary | null) => void;
}

export const useRelVolStatusStore = create<RelVolStatusState>((set, get) => ({
  vest: null,
  cme: null,
  setVest: (s) => {
    if (!sameSummary(get().vest, s)) set({ vest: s });
  },
  setCme: (s) => {
    if (!sameSummary(get().cme, s)) set({ cme: s });
  },
}));

/**
 * One line for the settings rows, or null when there is nothing to say:
 * history loading / failed, forced time of day without enough sessions, or
 * auto running entirely on the trailing mean.
 */
export function baselineHintText(p: {
  baseline: RelVolBaseline;
  loading: boolean;
  error: string | null;
  summary: RelVolSummary | null;
}): string | null {
  if (p.baseline === 'trailing') return null;
  if (p.loading) return 'loading baseline history…';
  if (p.error) {
    return `baseline history unavailable (${p.error}): ${p.baseline === 'tod' ? 'time of day has no markers' : 'auto uses the previous N bars'}`;
  }
  const s = p.summary;
  if (!s) return null;
  if (p.baseline === 'tod' && s.misses > 0) {
    return `${todMissText(s.lastMissSamples ?? 0)} on ${s.misses} bar${s.misses === 1 ? '' : 's'}: no markers there`;
  }
  if (p.baseline === 'auto' && s.tod === 0 && s.trailing > 0) return 'auto: on the previous N bars (not enough time-of-day history)';
  return null;
}
